<template>
  <div>
    <EntitySearchPopover
      itemKey="id"
      :allowEmptySelection="allowEmptySelection"
      :allowSearchScopeChange="allowSearchScopeChange"
      :blacklist="blacklist"
      :disabled="disabled"
      :items="allItems"
      :loading="isLoading"
      :localFilterFields="['title']"
      :localFilter="localSearch"
      :matchingItems="matchingItems"
      :maxSelectable="maxSelectableSites"
      :multiple="multiple"
      :open="open"
      :placeholder="$t('common.search')"
      :resultsHeaderText="$t('common.site_other')"
      :searchScope="searchScope"
      :selectedItems="selectedSites"
      :showAdvancedOption="showAdvancedOption"
      :zIndex="zIndex"
      @clearAll="onClearAll"
      @limitExceeded="handleLimitExceeded"
      @openChange="open = $event"
      @remove="onRemove"
      @search="searchItems"
      @searchScopeChange="handleSearchScopeChange"
      @select="onSelect"
      @showAdvancedSearch="handleOpenAdvanceModal"
    >
      <template v-slot:default>
        <slot></slot>
      </template>

      <template v-slot:item="{ item, searchTerm, isSelected }">
        <SiteListItem
          class="pl-1 pr-3 py-0"
          :site="item"
          :searchTerm="searchTerm"
          :selected="isSelected"
          @click="isSelected ? onRemove(item) : onSelect(item)"
        />
      </template>

      <template v-slot:selectedItem="{ item, searchTerm }">
        <SiteListItem
          class="pl-1 pr-3 py-0"
          :site="item"
          :searchTerm="searchTerm"
          :selected="multiple || allowEmptySelection"
          @click="allowEmptySelection || multiple ? onRemove(item) : null"
        />
      </template>
    </EntitySearchPopover>

    <AdvancedSitesSelectorModal
      v-if="showAdvancedModal"
      :filters="filters"
      :initialSites="selectedSites"
      :maxSelectableSites="maxSelectableSites"
      :show="showAdvancedModal"
      @close="showAdvancedModal = false"
      @select="handleSelectFromAdvancedModal"
      @cacheUpdate="handleAdvancedCacheUpdate"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import constants from '@/constants.js'
import mixin from '@/mixins/mixin.vue'
import { fuseSearch } from '@/lib/fuseSearch'

import EntitySearchPopover from '@/components/EntitySearchPopover.vue'
import SiteListItem from './SiteListItem.vue'
import AdvancedSitesSelectorModal from './AdvancedSitesSelectorModal.vue'

const ITEMS_PER_PAGE = 30

export default {
  name: 'SitesSearchPopover',

  mixins: [mixin],

  components: {
    EntitySearchPopover,
    SiteListItem,
    AdvancedSitesSelectorModal,
  },

  props: {
    allowEmptySelection: {
      type: Boolean,
      default: true,
    },
    allowSearchScopeChange: {
      type: Boolean,
      default: false,
    },
    availableLocalSites: {
      type: Array,
      default: () => [],
    },
    blacklist: {
      type: Array,
      default: () => [],
    },
    disabled: Boolean,
    filters: {
      type: Object,
      default: () => ({}),
    },
    localSearch: Boolean,
    maxSelectableSites: {
      type: Number,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    selectedSiteIds: {
      type: Array,
      default: () => [],
    },
    showAdvancedOption: {
      type: Boolean,
      default: false,
    },
    zIndex: {
      type: Number,
      default: 203,
    },
    targetOrganizationId: {
      type: String,
      default: null,
    },
    initialSearchScope: {
      type: String,
      default: constants.SEARCH_SCOPES.organization,
    },
  },

  data() {
    return {
      availableSites: [],
      cachedSites: {},
      initialSites: [],
      initialLoadExecuted: false,
      initialLoadFinished: false,
      isLoading: false,
      open: false,
      searchScope: this.initialSearchScope,
      searchTerm: '',
      showAdvancedModal: false,
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'organizationSites']),

    organizationIdToUse() {
      return this.targetOrganizationId || this.organizationId
    },

    organizationSitesMap() {
      return new Map(
        (this.organizationSites || []).map(site => [
          site?.id ?? site?._id,
          this.normalizeSite(site),
        ])
      )
    },

    allItems() {
      return this.availableSitesFiltered
    },

    availableSitesFiltered() {
      if (!this.blacklist?.length) return this.availableSites

      const blacklistSet = new Set(this.blacklist)
      return this.availableSites.filter(site => !blacklistSet.has(site.id))
    },

    localSitesParsed() {
      return this.parseSites(this.availableLocalSites)
    },

    selectedSites() {
      if (!this.multiple) {
        if (!this.selectedSiteIds.length) return []

        const site =
          this.cachedSites[this.selectedSiteIds[0]] ||
          this.organizationSitesMap.get(this.selectedSiteIds[0])
        return site ? [site] : []
      }

      const selected = _.compact(
        this.selectedSiteIds.map(
          id => this.cachedSites[id] ?? this.organizationSitesMap.get(id)
        )
      )

      return _.orderBy(
        selected,
        item => item.title?.toLowerCase?.() ?? '',
        'asc'
      )
    },

    matchingItems() {
      const selectedIdsSet = new Set(this.selectedSiteIds)
      return this.availableSitesFiltered.map(site => ({
        ...site,
        isSelected:
          selectedIdsSet.has(site.id) &&
          (this.multiple || this.allowEmptySelection),
      }))
    },
  },

  watch: {
    availableLocalSites: {
      immediate: true,
      handler(newSites) {
        if (!this.localSearch) return
        const parsed = this.parseSites(newSites)
        this.cachedSites = {}
        this.availableSites = parsed
        this.addSitesToCache(parsed)
      },
    },

    availableSites(newSites) {
      this.addSitesToCache(newSites)
    },

    initialSites(newSites) {
      this.addSitesToCache(newSites)
    },

    selectedSiteIds: {
      immediate: true,
      handler(value) {
        if (
          !this.initialLoadExecuted &&
          Array.isArray(value) &&
          value.length &&
          !this.localSearch
        ) {
          this.initialLoadExecuted = true
          this.getInitialSites()
        }
      },
    },

    filters: {
      handler(newVal, oldVal) {
        if (_.isEqual(newVal, oldVal)) return
        this.searchItems(this.searchTerm)
      },
      deep: true,
    },

    open(isOpening) {
      if (!this.initialLoadFinished && isOpening) {
        this.initialLoadFinished = true
        if (!this.localSearch) {
          this.searchItems()
        }
      }
    },
  },

  methods: {
    normalizeSite(site) {
      if (!site) return null
      const id = site?._id ?? site?.id
      if (!id) return null

      return {
        ...site,
        id,
        _id: id,
        title: site?.title ?? site?.name ?? '',
        subtitle:
          site?.subtitle ??
          (site?.countryCodes?.length
            ? this.$t(`countries.${site.countryCodes[0]}`)
            : site?.code ?? ''),
      }
    },

    parseSites(sites = []) {
      return _.compact(sites?.map?.(site => this.normalizeSite(site)))
    },

    addSitesToCache(sites = []) {
      const parsed = this.parseSites(sites)
      if (!parsed.length) return

      this.cachedSites = { ...this.cachedSites, ..._.keyBy(parsed, 'id') }
    },

    handleLimitExceeded({ limit }) {
      this.showError(
        this.$t('common.selection_limit_error', {
          selectionLimit: limit,
          type: this.$t('common.site_other'),
        })
      )
    },

    async getInitialSites() {
      try {
        const ids = _.compact(this.selectedSiteIds)
        if (!ids.length) return

        const missingIds = ids.filter(id => !this.cachedSites[id])

        const localMatches = missingIds
          .map(id => this.organizationSitesMap.get(id))
          .filter(Boolean)

        if (localMatches.length) {
          this.addSitesToCache(localMatches)
        }

        const stillMissing = missingIds.filter(id => !this.cachedSites[id])
        if (!stillMissing.length) return

        this.$emit('loading', true)

        const response = await api.site.getOrganizationSites(
          this.organizationIdToUse,
          { ids: stillMissing }
        )

        const parsed = this.parseSites(response?.data ?? [])
        this.initialSites = _.uniqBy([...this.initialSites, ...parsed], 'id')
      } catch (error) {
        this.showError(error)
      } finally {
        this.$emit('loading', false)
      }
    },

    async searchItems(terms = '') {
      if (this.localSearch) {
        this.searchTerm = terms
        this.availableSites = terms
          ? fuseSearch(this.localSitesParsed, ['title'], terms)
          : this.localSitesParsed
        return
      }

      try {
        this.isLoading = true
        this.searchTerm = terms

        const response = await api.site.getStreamSites(
          this.organizationIdToUse,
          {
            filters: this.filters,
            limit: ITEMS_PER_PAGE,
            from: 0,
            terms,
            sort: {
              by: terms?.length ? '_score' : 'name',
              order: terms?.length ? 'desc' : 'asc',
            },
          }
        )

        const results =
          response?.data?.results?.map(result => result.site) ?? []
        this.availableSites = this.parseSites(results)
      } catch (error) {
        this.showError(error)
      } finally {
        this.isLoading = false
      }
    },

    onSelect(site) {
      const normalizedSite = this.normalizeSite(site)
      if (!normalizedSite) return

      if (!this.multiple) {
        this.$emit('change', {
          ids: [normalizedSite.id],
          items: [normalizedSite],
        })

        this.open = false
        this.searchTerm = ''
        return
      }

      const nextIds = _.uniq([
        ...(this.selectedSiteIds || []),
        normalizedSite.id,
      ])

      if (this.maxSelectableSites && nextIds.length > this.maxSelectableSites) {
        this.showError(
          this.$t('common.selection_limit_error', {
            selectionLimit: this.maxSelectableSites,
            type: this.$t('common.site_other'),
          })
        )
        return
      }

      const items = _.uniqBy([...this.selectedSites, normalizedSite], 'id')

      this.$emit('change', {
        ids: nextIds,
        items,
      })
    },

    onRemove(site) {
      const id = site?.id ?? site?._id
      if (!id) return

      const ids = (this.selectedSiteIds || []).filter(siteId => siteId !== id)
      const items = this.selectedSites.filter(
        selectedSite => selectedSite.id !== id
      )

      this.$emit('change', { ids, items })
    },

    onClearAll() {
      this.$emit('change', { ids: [], items: [] })
    },

    handleSearchScopeChange(searchScope) {
      if (this.isLoading) return
      this.searchScope = searchScope
      if (!this.localSearch) {
        this.searchItems(this.searchTerm)
      }
    },

    handleOpenAdvanceModal() {
      this.showAdvancedModal = true
      this.open = false
    },

    handleSelectFromAdvancedModal(selection) {
      this.showAdvancedModal = false
      this.$emit('change', selection || { ids: [], items: [] })
    },

    handleAdvancedCacheUpdate(sites) {
      this.addSitesToCache(sites)
    },
  },
}
</script>

<style scoped></style>
